import { fetchMe } from './api/auth.js';
import { listItems, createItem } from './api/items.js';
import { isEnabled } from './features/flags.js';

const FEATURE = 'live-items';

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function badgeClass(status) {
  return status === 'done' || status === 'in_progress' ? 'active' : 'pending';
}

function renderItemList(items, container, onSelect) {
  if (!items.length) {
    container.innerHTML = '<p class="empty">No items yet. Create one to get started.</p>';
    return;
  }

  container.innerHTML = items
    .map(
      (item) => `
        <div class="list-item live-item" data-item-id="${escapeHtml(item.id)}" role="button" tabindex="0">
          <strong>${escapeHtml(item.title)}</strong>
          <span class="meta">
            <span class="badge ${badgeClass(item.status)}">${escapeHtml(item.status)}</span>
            · ${escapeHtml(item.priority)}${item.due_date ? ' · Due ' + escapeHtml(item.due_date) : ''}
          </span>
        </div>
      `
    )
    .join('');

  container.querySelectorAll('.live-item').forEach((el) => {
    el.addEventListener('click', () => onSelect(el.dataset.itemId));
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(el.dataset.itemId);
      }
    });
  });
}

function updateDetailCard(item) {
  const card = document.getElementById('journey-user-detail');
  if (!card) return;

  const assignees = item.assignees?.map((a) => escapeHtml(a.name)).join(', ') || 'Unassigned';
  const due = item.due_date ? escapeHtml(item.due_date) : 'No due date';

  card.innerHTML = `
    <h3>Item detail</h3>
    <div class="detail-card">
      <div class="detail-row"><span>Title</span><strong>${escapeHtml(item.title)}</strong></div>
      <div class="detail-row"><span>Status</span><span class="badge ${badgeClass(item.status)}">${escapeHtml(item.status)}</span></div>
      <div class="detail-row"><span>Priority</span><strong>${escapeHtml(item.priority)}</strong></div>
      <div class="detail-row"><span>Assignees</span><strong>${assignees}</strong></div>
      <div class="detail-row"><span>Due</span><strong>${due}</strong></div>
      ${item.description ? `<div class="detail-row"><span>Description</span><p>${escapeHtml(item.description)}</p></div>` : ''}
    </div>
    <button class="btn-secondary" data-journey="user-reports">Next: reports →</button>
  `;
}

async function loadInbox(workspaceId) {
  const panel = document.getElementById('journey-user-inbox');
  if (!panel) return;
  const list = panel.querySelector('.list');
  if (!list) return;

  list.innerHTML = '<p class="empty">Loading items…</p>';
  try {
    const response = await listItems(workspaceId, { assigned_to_me: true });
    const items = response.data || [];
    renderItemList(items, list, (id) => {
      const selected = items.find((i) => i.id === id);
      if (selected) updateDetailCard(selected);
      document
        .querySelector('[data-journey="user-detail"]')
        ?.click();
    });
  } catch (err) {
    list.innerHTML = `<p class="empty">Could not load items: ${escapeHtml(err.message)}</p>`;
  }
}

function wireCreateForm(workspaceId) {
  const panel = document.getElementById('journey-user-create');
  if (!panel) return;
  const button = panel.querySelector('.btn-primary');
  if (!button || button.dataset.wired) return;
  button.dataset.wired = 'true';

  button.addEventListener('click', async (e) => {
    e.preventDefault();
    const title = panel.querySelector('input[type="text"]')?.value.trim();
    const description = panel.querySelector('textarea')?.value.trim();
    const priority = panel.querySelector('select')?.value.toLowerCase() || 'medium';

    if (!title) {
      alert('Title is required');
      return;
    }

    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Creating…';

    try {
      const item = await createItem(workspaceId, {
        title,
        description: description || undefined,
        priority,
        status: 'todo',
        visibility: 'private',
      });
      updateDetailCard(item);
      await loadInbox(workspaceId);

      // Reset the create form so the next visit starts fresh.
      const input = panel.querySelector('input[type="text"]');
      if (input) input.value = '';
      const textarea = panel.querySelector('textarea');
      if (textarea) textarea.value = '';
      const select = panel.querySelector('select');
      if (select) select.value = 'Medium';

      document.querySelector('[data-journey="user-detail"]')?.click();
    } catch (err) {
      alert(`Create failed: ${err.message}`);
    } finally {
      button.disabled = false;
      button.textContent = originalText;
    }
  });
}

function showIndicator() {
  const footer = document.querySelector('.footer');
  if (!footer) return;
  const span = document.createElement('span');
  span.className = 'live-indicator';
  span.textContent = '● Live items demo';
  span.setAttribute('aria-live', 'polite');
  footer.prepend(span);
}

export async function initItemsDemo() {
  if (!isEnabled(FEATURE)) return;

  try {
    const me = await fetchMe();
    const membership = me.memberships?.[0];
    if (!membership?.workspace_id) {
      console.warn('live-items: no workspace membership found');
      return;
    }
    const workspaceId = membership.workspace_id;

    showIndicator();
    await loadInbox(workspaceId);
    wireCreateForm(workspaceId);
  } catch (err) {
    console.error('live-items demo init failed', err);
  }
}
